'use client';

import { Button } from '@/components/ui/buttons/Button';
import { useTranslation } from '@/features/i18n';
import { Input } from '@/ui/form/Input';
import { Label } from '@/ui/form/Label';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuthentication } from '@vimond/features/authentication';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export const SignUpPasswordForm = ({
  confirmCode,
  email = '',
  logIn,
  password = '',
  setEmail,
  setPassword,
}: {
  confirmCode: () => void;
  email: string;
  logIn: (email?: string, password?: string) => void;
  password: string;
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
}) => {
  const { signUp } = useAuthentication();
  const { t } = useTranslation();

  const FormSchema = z.object({
    email: z.string().email(t('auth.validate.invalid-email-address')),
    password: z
      .string()
      .min(8, t('auth.validate.password-must-be-at-least-8-charaters-long')),
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  type FormSchemaType = z.infer<typeof FormSchema>;
  const {
    formState: { errors, isDirty, isLoading, isSubmitting },
    handleSubmit,
    register,
  } = useForm<FormSchemaType>({
    resolver: zodResolver(FormSchema),
    values: {
      email,
      password,
    },
  });

  const onSubmit: SubmitHandler<FormSchemaType> = async (data) => {
    const { email, password } = data;
    try {
      const res = await signUp({ email, password });
      if (res.isSignUpComplete) {
        setEmail(email);
        setPassword(password);
        // login past sign up needs send email and password as parameters
        // state updates happen after the login method is called
        // state updates still needed for email and password if sign in code flow is enabled
        logIn(email, password);
      } else if (res.nextStep) {
        setEmail(email);
        setPassword(password);
        confirmCode();
      }
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error?.message);
      }
    }
  };

  return (
    <form className="space-y-4 md:space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <Label htmlFor="email">{t('auth.email-address')}</Label>
        <div className="mt-2">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            {...register('email')}
            placeholder={t('auth.email-placeholder')}
            aria-describedby="email-error"
          />
        </div>
        <p
          id="email-error"
          className="text-red-500"
          aria-live="polite"
          aria-atomic="true"
        >
          {errors.email?.message}
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="password">{t('auth.password')}</Label>
        </div>
        <div className="mt-2">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            {...register('password')}
            placeholder="********"
            aria-describedby="password-error"
          />
        </div>
        <p
          id="password-error"
          className="text-red-500"
          aria-live="polite"
          aria-atomic="true"
        >
          {errors.password?.message}
        </p>
      </div>

      <Button
        variant="primary"
        size="sm"
        disabled={isLoading || isSubmitting || !isDirty}
        type="submit"
        className="w-full"
      >
        {t('sign-up')}
      </Button>
      {errorMessage && <p className="text-red-500">{errorMessage}</p>}
    </form>
  );
};
